import { ExpiryDetail, ExpiryMarker, RowCell } from "@don/design-system";

import type { Column } from "./columns.js";
import type { ExpiryMarker as Marker } from "./row.js";

/**
 * The Video column (design-spec §4 "Session row", D20, issue 195): empty over 10 days, the
 * hourglass at 10 or fewer, the red clock at 5 or fewer, `Video deleted` once the video has
 * gone. The level and the words are the rules' (`markerFor`), so the shape and the count
 * cannot disagree; this only places them on the Sessions grid.
 *
 * The cell lets its marker run past the column rather than clip a glyph (E's `ve-c-exp`): the
 * Video column is the row's last, so the overflow lands in the row's own padding.
 */
export function ExpiryCell({
  column,
  marker,
  sessionId,
  expanded,
  onToggle,
}: {
  column: Column;
  marker: Marker;
  sessionId: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <RowCell data-column={column.key} overflow="visible" title={marker.text || undefined}>
      <ExpiryMarker
        level={marker.level}
        expanded={expanded}
        data-expiry-for={sessionId}
        aria-label={marker.level === "urgent" ? `${marker.text}. Show details` : undefined}
        onToggle={(event) => {
          // **The row is the click target and this control is inside it.** Without this the
          // marker would also open the session, so the one control on the row that acts
          // rather than navigates would navigate too.
          event.stopPropagation();
          onToggle();
        }}
      >
        {/* The words are the marker's baseline item, as in the Summary cell, so they sit on
            the row's baseline rather than the glyph's bottom edge doing so. */}
        <span style={{ alignSelf: "baseline" }}>{marker.short}</span>
      </ExpiryMarker>
    </RowCell>
  );
}

/**
 * The open red marker's detail, spanning the whole row below its cells (design-spec §4,
 * `sessions-expiry-open`): the sentence, and a small primary `Renew` only where a renewal has
 * somewhere to go. Clicks inside it stay in it, so reading the sentence never opens the session.
 */
export function ExpiryRowDetail({
  sessionId,
  detail,
  onRenew,
}: {
  sessionId: string;
  detail: string;
  onRenew?: (sessionId: string) => void;
}) {
  return (
    <div
      data-expiry-detail-for={sessionId}
      style={{ gridColumn: "1 / -1" }}
      onClick={(event) => event.stopPropagation()}
    >
      <ExpiryDetail
        action={
          onRenew === undefined
            ? undefined
            : {
                children: "Renew",
                "data-expiry-renew": sessionId,
                onClick: () => onRenew(sessionId),
              }
        }
      >
        {detail}
      </ExpiryDetail>
    </div>
  );
}
